// Bloques Bocetta dentro de las secciones de Horizon. Cada bloque es un elemento propio que arranca solo
// al aparecer en la página, también cuando el editor de temas lo vuelve a pintar.
// Los textos vienen de los ajustes del bloque (atributos data-*), no de este archivo.
import {
  COLORES, ORDEN, pesos, quieto, agregarAlCarrito, lineaLibreta, aparecer, cargar3d, alVerse, sin3d,
  ponerColor, iniciarColor, formularioShopify, correoValido, validarCorreo, suscribir, RUTAS
} from '@bocetta/base.js';
import { PRECIO, tachado, urlColor } from '@bocetta/datos.js';

const B = window.BOCETTA || {};
const TXT = Object.assign({ comprar: 'Add to cart', pronto: 'Coming soon', avisar: 'Notify me', aviso: 'Bocetta [color] is coming soon. Leave your email and we’ll write the day it’s out.', avisoOk: 'Done. You’ll hear from us the day Bocetta [color] is out.', correo: 'Your email', correoMal: 'That email doesn’t look complete (name@email.com).' },
  Object.fromEntries(Object.entries(B.textos || {}).filter(([, v]) => v)));
const conColor = (txt, k) => String(txt || '').replaceAll('[color]', COLORES[k]?.nombre || '');

class Bloque extends HTMLElement {
  connectedCallback() {
    if (this.listo) return;
    this.listo = true;
    this.$ = (s) => this.querySelector(s);
    this.iniciar();
    aparecer(this);
  }
  iniciar() {}
}
const definir = (nombre, clase) => { if (!customElements.get(nombre)) customElements.define(nombre, clase); };

/* ---------- Color elegido en el selector de variantes de Horizon ---------- */

const claveDe = (valor) => {
  const v = String(valor || '').trim().toLowerCase();
  return ORDEN.find((k) => k === v || COLORES[k].nombre.toLowerCase() === v || COLORES[k].en.toLowerCase() === v) || null;
};
function colorElegido() {
  for (const i of document.querySelectorAll('variant-picker input:checked, variant-picker select')) {
    const k = claveDe(i.value);
    if (k) return k;
  }
  const id = new URLSearchParams(location.search).get('variant');
  if (id) { const k = ORDEN.find((c) => String(COLORES[c].variante) === id); if (k) return k; }
  return claveDe(new URLSearchParams(location.search).get('color'));
}
const oyentesColor = new Set();
function alCambiarColor(fn) {
  oyentesColor.add(fn);
  if (oyentesColor.size > 1) return;
  const avisar = () => { const k = colorElegido(); if (k) oyentesColor.forEach((f) => f(k)); };
  document.addEventListener('change', (e) => { if (e.target.closest?.('variant-picker')) avisar(); });
  document.addEventListener('shopify:product:select', () => requestAnimationFrame(avisar));
}

/* ---------- Libreta en 3D ---------- */
definir('bocetta-libreta-3d', class extends Bloque {
  iniciar() {
    const d = this.dataset, lienzo = this.$('[data-lienzo-3d]');
    const muestras = this.$('[data-muestras-3d]');
    let sel = (d.seguir === 'true' && colorElegido()) || d.color || 'nero';
    let visor = null;

    const marcar = (k) => muestras?.querySelectorAll('[data-muestra]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.muestra === k)));
    if (muestras) {
      muestras.innerHTML = ORDEN.map((k) => `<button type="button" class="porque__muestra" data-muestra="${k}" style="--muestra:${COLORES[k].hex}" aria-label="${COLORES[k].nombre}" aria-pressed="${k === sel}"></button>`).join('');
      muestras.addEventListener('click', (e) => {
        const b = e.target.closest('[data-muestra]');
        if (!b) return;
        sel = b.dataset.muestra; marcar(sel); visor?.color(sel); ponerColor(sel, true);
      });
    }
    if (d.seguir === 'true') {
      alCambiarColor((k) => { sel = k; marcar(k); visor?.color(k); ponerColor(k); });
      ponerColor(sel);
    }
    if (sin3d()) return;

    alVerse(lienzo, () => cargar3d().then(({ libreta3d, abanico3d }) => {
      if (d.modo === 'abanico') {
        abanico3d(lienzo, ORDEN, { quieto: quieto(), abiertas: d.abiertas === 'true', alElegir: (k) => { location.href = urlColor(k); } });
        return;
      }
      visor = libreta3d(lienzo, sel, {
        quieto: quieto(),
        abierta: d.modo !== 'cerrada',
        controles: d.controles === 'true',
        cerca: Number(d.cerca) || 1.08,
        ciclo: d.ciclo === 'true' ? ORDEN : null,
        pliegos: 3
      });
      lienzo.addEventListener('libreta3d-color', (e) => marcar(e.detail));
    }).catch(() => {}));
  }
});

/* ---------- Tarjetas de los cuatro colores ---------- */
function avisoHTML(k, id) {
  return `
    <form class="aviso boletin campo" action="/contact" method="post" data-aviso="${k}">
      <p class="aviso__txt" id="${id}-txt">${conColor(TXT.aviso, k)}</p>
      <div class="boletin__fila">
        <label for="${id}" class="visualmente-oculto">${TXT.correo}</label>
        <input id="${id}" class="control" type="email" placeholder="${TXT.correo}" autocomplete="email" aria-describedby="${id}-txt">
        <button type="submit" class="btn btn--claro">${TXT.avisar}</button>
      </div>
    </form>`;
}
function activarAviso(form, ok) {
  formularioShopify(form, {
    validar: (f) => { const i = f.querySelector('input[type="email"]'); return correoValido(i.value) ? [] : [[i, TXT.correoMal]]; },
    extra: suscribir(`newsletter, aviso-${form.dataset.aviso}`),
    alEnviar: (f) => { f.outerHTML = `<p class="confirmacion" role="status">${conColor(ok || TXT.avisoOk, f.dataset.aviso)}</p>`; }
  });
}

definir('bocetta-colores', class extends Bloque {
  iniciar() {
    const tienda = this.$('[data-tienda]');
    tienda.innerHTML = ORDEN.map((k) => {
      const c = COLORES[k];
      return `
        <article class="producto-tarjeta sobre-color" style="--c:${c.hex}; --suave:${c.suave}" aria-labelledby="tarjeta-${k}" data-aparece>
          <figure class="producto-tarjeta__foto" data-tarjeta-modelo="${k}">
            ${c.pronto ? `<span class="insignia-pronto">${TXT.pronto}</span>` : ''}
            <img src="${c.cerrada}" alt="" width="815" height="1143" loading="lazy" decoding="async">
          </figure>
          <div class="producto-tarjeta__info">
            <h3 id="tarjeta-${k}" class="producto-tarjeta__nombre"><a href="${urlColor(k)}">Bocetta ${c.nombre}</a></h3>
            <p class="producto-tarjeta__frase">${c.frase}</p>
            <p class="producto-tarjeta__precio">${c.pronto ? TXT.pronto : `${pesos(PRECIO)} ${tachado(1)}`}</p>
          </div>
          ${c.pronto
            ? `<button type="button" class="btn btn--linea-clara btn--ancho" data-avisar="${k}">${TXT.avisar}</button>`
            : `<button type="button" class="btn btn--claro btn--ancho" data-comprar="${k}">${TXT.comprar}</button>`}
        </article>`;
    }).join('');

    let n = 0;
    tienda.addEventListener('click', (e) => {
      const foto = e.target.closest('[data-tarjeta-modelo]');
      if (foto) { location.href = urlColor(foto.dataset.tarjetaModelo); return; }
      const comprar = e.target.closest('[data-comprar]');
      if (comprar) agregarAlCarrito([lineaLibreta(comprar.dataset.comprar)], comprar);
      const avisar = e.target.closest('[data-avisar]');
      if (avisar) {
        const tarjeta = avisar.closest('article');
        avisar.outerHTML = avisoHTML(avisar.dataset.avisar, `aviso-tarjeta-${++n}`);
        activarAviso(tarjeta.querySelector('[data-aviso]'));
        tarjeta.querySelector('[data-aviso] input').focus();
      }
    });

    if (this.dataset.tresD !== 'false' && !sin3d()) {
      alVerse(tienda, () => cargar3d().then(({ libreta3d }) => {
        tienda.querySelectorAll('[data-tarjeta-modelo]').forEach((f) => libreta3d(f, f.dataset.tarjetaModelo, { quieto: quieto(), abierta: false, alternar: true, pliegos: 3, desfase: 0, cerca: 1.28 }));
      }).catch(() => {}));
    }
  }
});

/* ---------- «Notify me» en la página de producto ---------- */
definir('bocetta-aviso', class extends Bloque {
  iniciar() {
    const d = this.dataset;
    const pintar = (k) => {
      const c = COLORES[k];
      const mostrar = !!(c && c.pronto);
      if (!window.Shopify?.designMode) this.hidden = !mostrar;
      if (!mostrar && !window.Shopify?.designMode) return;
      const clave = c ? k : 'rosso';
      this.innerHTML = `
        <form class="aviso boletin campo" action="/contact" method="post" data-aviso="${clave}">
          <p class="aviso__txt" id="aviso-ficha-txt">${conColor(d.texto, clave)}</p>
          <div class="boletin__fila">
            <label for="aviso-ficha" class="visualmente-oculto">${d.placeholder}</label>
            <input id="aviso-ficha" class="control" type="email" placeholder="${d.placeholder}" autocomplete="email" aria-describedby="aviso-ficha-txt">
            <button type="submit" class="button">${d.boton}</button>
          </div>
        </form>`;
      activarAviso(this.$('[data-aviso]'), d.ok);
    };
    pintar(colorElegido());
    alCambiarColor(pintar);
  }
});

/* ---------- Fondo vivo ---------- */
definir('bocetta-cielo', class extends Bloque {
  iniciar() {
    const lienzo = this.$('canvas');
    const arrancar = () => import('@bocetta/cielo.js').then(({ cieloVivo }) => cieloVivo(lienzo, { quieto: quieto() })).catch(() => {});
    if (document.readyState === 'complete') (window.requestIdleCallback || setTimeout)(arrancar);
    else addEventListener('load', () => (window.requestIdleCallback || setTimeout)(arrancar), { once: true });
  }
});

/* ---------- Quiz ---------- */
definir('bocetta-quiz', class extends Bloque {
  iniciar() {
    const t = this.dataset, raiz = this.$('[data-quiz]');
    // Preguntas y respuestas: los bloques hijos (ocultos) traen sus datos
    const PREGUNTAS = [...this.querySelectorAll('[data-pregunta]')].map((p) => ({
      q: p.dataset.texto,
      o: [...p.querySelectorAll('[data-opcion]')].map((o) => ({
        etiqueta: o.dataset.etiqueta, pista: o.dataset.pista, tinta: o.dataset.tinta,
        puntos: [[o.dataset.color, Number(o.dataset.puntos)], [o.dataset.color2, Number(o.dataset.puntos2)]].filter(([k]) => COLORES[k])
      }))
    })).filter((p) => p.o.length);
    if (!PREGUNTAS.length) return;
    const razon = (k) => t['razon' + k[0].toUpperCase() + k.slice(1)];
    const consejo = (tinta) => t['tinta' + tinta[0].toUpperCase() + tinta.slice(1)];
    let respuestas = [];
    let recomendado = null;

    const calcular = () => {
      const puntos = { nero: 0, blu: 0, rosso: 0, viola: 0 };
      let tinta = 'nose';
      respuestas.forEach(([p, i]) => {
        const o = PREGUNTAS[p].o[i];
        if (o.tinta) tinta = o.tinta;
        o.puntos.forEach(([k, n]) => { puntos[k] += n; });
      });
      const orden = ORDEN.slice().sort((a, b) => puntos[b] - puntos[a]);
      return { primero: orden[0], segundo: orden[1], tinta, disponible: orden.find((k) => !COLORES[k].pronto) || 'nero' };
    };

    const pintarPregunta = () => {
      const paso = respuestas.length, p = PREGUNTAS[paso], total = PREGUNTAS.length;
      raiz.innerHTML = `
        <div class="quiz__paso">
          <div class="quiz__estado">
            <div class="quiz__fila">
              <span>${t.paso.replace('[n]', paso + 1).replace('[total]', total)}</span>
              ${paso > 0 ? `<button type="button" class="enlace" data-atras>${t.atras}</button>` : ''}
            </div>
            <div class="progreso" role="progressbar" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${paso + 1}"><span style="width:${((paso + 1) / total) * 100}%"></span></div>
            ${paso === 0 && t.intro ? `<p class="quiz__intro">${t.intro}</p>` : ''}
          </div>
          <h2 class="quiz__pregunta" tabindex="-1">${p.q}</h2>
          <div class="quiz__opciones">
            ${p.o.map((o, i) => `
              <button type="button" class="opcion" data-clave="${paso}:${i}">
                <span class="opcion__txt">
                  <span class="opcion__etiqueta">${o.etiqueta}</span>
                  ${o.pista ? `<span class="opcion__pista">${o.pista}</span>` : ''}
                </span>
              </button>`).join('')}
          </div>
        </div>`;
    };

    const pintarResultado = () => {
      const { primero: ideal, segundo, tinta, disponible } = calcular();
      // Siempre recomendamos un color que se puede pedir hoy; si el ideal todavía no sale, queda como alternativa con «Avísame»
      const primero = COLORES[ideal].pronto ? disponible : ideal;
      const alterno = COLORES[ideal].pronto ? ideal : segundo;
      const c = COLORES[primero], s = COLORES[alterno];
      recomendado = primero;
      ponerColor(primero, true);
      raiz.innerHTML = `
        <div class="resultado" style="--c:${c.hex}">
          <div class="resultado__texto sobre-color">
            <div class="resultado__arriba">
              <p class="antetitulo">${t.resultado}</p>
              <h2 class="resultado__nombre" tabindex="-1">${c.nombre}</h2>
              <p class="resultado__porque">${razon(primero) || ''}</p>
              <p class="resultado__tinta">${consejo(tinta) || ''}</p>
            </div>
            <div class="resultado__abajo">
              <div class="resultado__botones">
                <button type="button" class="btn btn--claro" data-agregar>${conColor(t.agregar, primero)} · ${pesos(PRECIO)}</button>
                <a class="btn btn--linea-clara" href="${urlColor(primero)}">${conColor(t.ver, primero)}</a>
              </div>
              ${alterno === primero ? '' : s.pronto ? `
              <p class="resultado__segunda">${conColor(t.segunda, alterno)} · ${TXT.pronto}</p>
              ${avisoHTML(alterno, 'aviso-quiz')}` : `
              <a class="enlace resultado__segunda" href="${urlColor(alterno)}">${conColor(t.segunda, alterno)}</a>`}
              <div class="resultado__correo" data-correo-bloque>
                <form class="boletin campo" action="/contact" method="post" data-correo-quiz>
                  <p id="quiz-correo-txt">${t.correo}</p>
                  <div class="boletin__fila">
                    <label for="quiz-correo" class="visualmente-oculto">${TXT.correo}</label>
                    <input id="quiz-correo" class="control" type="email" placeholder="${TXT.correo}" autocomplete="email" aria-describedby="quiz-correo-txt">
                    <button type="submit" class="btn btn--linea-clara">${t.correoBoton}</button>
                  </div>
                </form>
                <button type="button" class="enlace" data-no-gracias style="align-self: flex-start">${t.correoNo}</button>
              </div>
              <button type="button" class="enlace" data-reiniciar style="align-self: flex-start">${t.reiniciar}</button>
            </div>
          </div>
          <figure class="resultado__foto" data-resultado-modelo>
            <img src="${c.portada}" alt="Bocetta ${c.nombre}" width="611" height="856">
          </figure>
        </div>`;
      const aviso = raiz.querySelector('[data-aviso]');
      if (aviso) activarAviso(aviso);
      const fig = raiz.querySelector('[data-resultado-modelo]');
      if (!sin3d()) cargar3d().then(({ libreta3d }) => libreta3d(fig, primero, { abierta: false, quieto: quieto(), cerca: 1.15 })).catch(() => {});
      formularioShopify(raiz.querySelector('[data-correo-quiz]'), {
        validar: validarCorreo,
        extra: suscribir(`newsletter, quiz-${ideal}`),
        alEnviar: () => { raiz.querySelector('[data-correo-bloque]').innerHTML = `<p class="confirmacion" role="status">${t.correoOk}</p>`; }
      });
    };

    const pintar = (inicial = false) => {
      if (respuestas.length < PREGUNTAS.length) pintarPregunta(); else pintarResultado();
      if (inicial) return;
      const h2 = raiz.querySelector('h2');
      h2.focus({ preventScroll: true });
      // Solo se mueve la página si la pregunta quedó fuera de la vista (y nunca debajo de la cabecera fija)
      const r = h2.getBoundingClientRect(), cabecera = document.querySelector('#header-group')?.getBoundingClientRect().bottom || 0;
      if (r.top < cabecera || r.bottom > innerHeight) scrollBy({ top: r.top - cabecera - 24, behavior: quieto() ? 'auto' : 'smooth' });
    };

    raiz.addEventListener('click', (e) => {
      // Solo botones dentro del quiz: <bocetta-quiz> guarda sus textos en data-atras, data-agregar… y no cuenta como botón
      const dentro = (s) => { const el = e.target.closest(s); return el && raiz.contains(el) ? el : null; };
      const opcion = dentro('[data-clave]');
      if (opcion) { respuestas.push(opcion.dataset.clave.split(':').map(Number)); pintar(); return; }
      if (dentro('[data-atras]')) { respuestas.pop(); pintar(); return; }
      if (dentro('[data-reiniciar]')) { respuestas = []; pintar(); return; }
      if (dentro('[data-no-gracias]')) { raiz.querySelector('[data-correo-bloque]').remove(); raiz.querySelector('[data-reiniciar]').focus(); return; }
      const agregar = dentro('[data-agregar]');
      if (agregar) agregarAlCarrito([lineaLibreta(recomendado || calcular().disponible)], agregar);
    });

    pintar(true);
  }
});

/* ---------- Cotización para equipos ---------- */
definir('bocetta-cotizacion', class extends Bloque {
  iniciar() {
    const form = this.$('form');
    if (!form || !form.querySelector('[data-campo="empresa"]')) return;
    const t = this.dataset;
    formularioShopify(form, {
      validar: (f) => {
        f.querySelector('[data-colores]').value = [...f.querySelectorAll('[name="colores"]:checked')].map((i) => i.value).join(', ');
        const g = (k) => f.querySelector(`[data-campo="${k}"]`);
        const e = [];
        if (!g('empresa').value.trim()) e.push([g('empresa'), t.eEmpresa]);
        if (!g('nombre').value.trim()) e.push([g('nombre'), t.eNombre]);
        if (!correoValido(g('correo').value)) e.push([g('correo'), t.eCorreo]);
        if (!g('pais').value.trim()) e.push([g('pais'), t.ePais]);
        if (!(Number(g('cantidad').value) > 0)) e.push([g('cantidad'), t.eCantidad]);
        if (!f.querySelector('[name="colores"]:checked')) e.push([g('colores'), t.eColores]);
        if (!f.querySelector('[name="contact[Invoice]"]:checked')) e.push([g('factura'), t.eFactura]);
        return e;
      },
      alEnviar: (f) => {
        f.innerHTML = `${t.titulo ? `<h2 class="titulo-s">${t.titulo}</h2>` : ''}<p class="confirmacion ancho" role="status" tabindex="-1">${t.ok}</p>`;
        f.querySelector('.confirmacion').focus();
      }
    });
  }
});

/* ---------- Intro con el logo (página de inicio, una vez por visita) ---------- */
if (B.intro && B.plantilla === 'index' && !quieto() && !window.Shopify?.designMode) {
  let vista = true;
  try { vista = sessionStorage.getItem('bocetta-intro') === '1'; sessionStorage.setItem('bocetta-intro', '1'); } catch {}
  if (!vista) {
    const marca = B.logo ? `<img class="intro__logo" src="${B.logo}" alt="" width="1330" height="190">` : '<span>BOCETTA</span>';
    document.body.insertAdjacentHTML('beforeend', `<div class="bocetta"><div class="intro" aria-hidden="true">${marca}</div></div>`);
    const intro = document.querySelector('.intro');
    const quitar = () => { intro.classList.add('sale'); setTimeout(() => intro.parentElement.remove(), 700); };
    intro.addEventListener('click', quitar);
    setTimeout(quitar, 1100);
  }
}

iniciarColor();
export { RUTAS };
